import React, { useEffect, useMemo, useState } from "react";
import { api, fmtUSD, fmtPct, pdi, pdiTier } from "../lib/api";
import { Eye, ArrowDown, ArrowUp, Lock, Calculator, ChartLine, Package } from "@phosphor-icons/react";
import CategoryPackageModal from "./CategoryPackageModal";

const TIER_CLASS = {
  GREEN: "tier-GREEN",
  YELLOW: "tier-YELLOW",
  ORANGE: "tier-ORANGE",
  RED: "tier-RED",
  BLACK: "tier-BLACK",
};

export default function PerformanceLedger({ onOpenSavingsProof, onOpenSourcingLever }) {
  const [rows, setRows] = useState(null);
  const [drivers, setDrivers] = useState({});
  const [filter, setFilter] = useState("ALL");
  const [savingsMode, setSavingsMode] = useState("ADJUSTED"); // ADJUSTED | GROSS
  const [sort, setSort] = useState({ key: "risk_adjusted_delta", dir: "desc" });
  const [pkgOpen, setPkgOpen] = useState(false);
  const [pkgSegment, setPkgSegment] = useState({ code: null, name: null });

  const openPackage = (r) => {
    setPkgSegment({ code: r.segment_code, name: r.segment_name });
    setPkgOpen(true);
  };

  useEffect(() => {
    api.ledger({ limit: 200 }).then(setRows).catch(() => setRows([]));
    api.marketSegmentDrivers()
      .then((d) => setDrivers(Object.fromEntries((d.segments || []).map((s) => [s.segment_code, s]))))
      .catch(() => setDrivers({}));
  }, []);

  const filtered = useMemo(() => {
    if (!rows) return [];
    const f = filter === "ALL" ? rows : rows.filter((r) => r.category === filter);
    const sorted = [...f].sort((a, b) => {
      const av = a[sort.key], bv = b[sort.key];
      const cmp = typeof av === "string" ? av.localeCompare(bv) : av - bv;
      return sort.dir === "asc" ? cmp : -cmp;
    });
    return sorted;
  }, [rows, filter, sort]);

  return (
    <section
      data-testid="performance-ledger"
      className="bg-slate-900 border border-slate-800 flex flex-col h-full"
    >
      <header className="px-5 py-3 border-b border-slate-800 flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h2 className="font-display text-base font-semibold text-slate-50 flex items-center gap-2">
            <Lock weight="duotone" className="text-emerald-400" size={16} />
            Savings Audit & Verification · Decision Proof
          </h2>
          <p className="text-xs text-slate-500 mt-0.5 font-mono">
            Baseline (locked) ↔ Realized (federal award) ·{" "}
            <span className="text-emerald-400">Decision-Adjusted = Gross × (PDI ÷ 100)</span>{" "}
            · <span className="text-emerald-300">100 = MAX INTEGRITY</span> · click any row for Decision Proof
          </p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <div className="flex items-center gap-1 bg-slate-950 border border-slate-800 p-0.5 rounded-sm" data-testid="savings-mode-toggle">
            {[
              { id: "ADJUSTED", label: "Decision-Adj" },
              { id: "GROSS", label: "Gross" },
            ].map((opt) => (
              <button
                key={opt.id}
                onClick={() => {
                  setSavingsMode(opt.id);
                  setSort({ key: opt.id === "ADJUSTED" ? "risk_adjusted_delta" : "delta", dir: "desc" });
                }}
                data-testid={`savings-mode-${opt.id.toLowerCase()}`}
                className={`px-3 py-1 text-[11px] tracking-mono-label rounded-sm transition-colors ${
                  savingsMode === opt.id
                    ? "bg-emerald-500/20 text-emerald-300"
                    : "text-slate-400 hover:text-slate-200"
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-1 bg-slate-950 border border-slate-800 p-0.5 rounded-sm">
            {["ALL", "DIRECT", "INDIRECT"].map((opt) => (
              <button
                key={opt}
                onClick={() => setFilter(opt)}
                data-testid={`ledger-filter-${opt.toLowerCase()}`}
                className={`px-3 py-1 text-[11px] tracking-mono-label rounded-sm transition-colors ${
                  filter === opt
                    ? "bg-emerald-500/20 text-emerald-300"
                    : "text-slate-400 hover:text-slate-200"
                }`}
              >
                {opt}
              </button>
            ))}
          </div>
        </div>
      </header>

      <div className="overflow-auto thin-scroll max-h-[520px]">
        <table className="w-full text-xs" data-testid="ledger-table">
          <thead className="sticky top-0 bg-slate-950 z-10">
            <tr className="border-b border-slate-800">
              <Th onClick={() => setSort(s => ({ key: "po_number", dir: s.dir === "asc" ? "desc" : "asc" }))} sort={sort} k="po_number">PO #</Th>
              <Th>Supplier</Th>
              <Th>Agency</Th>
              <Th>Category</Th>
              <Th align="center">PDI</Th>
              <Th>Market Driver</Th>
              <Th onClick={() => setSort(s => ({ key: "baseline", dir: s.dir === "asc" ? "desc" : "asc" }))} sort={sort} k="baseline" align="right">Baseline</Th>
              <Th onClick={() => setSort(s => ({ key: "realized", dir: s.dir === "asc" ? "desc" : "asc" }))} sort={sort} k="realized" align="right">Realized</Th>
              <Th onClick={() => setSort(s => ({ key: "delta", dir: s.dir === "asc" ? "desc" : "asc" }))} sort={sort} k="delta" align="right">Gross Δ</Th>
              <Th onClick={() => setSort(s => ({ key: "risk_adjusted_delta", dir: s.dir === "asc" ? "desc" : "asc" }))} sort={sort} k="risk_adjusted_delta" align="right">
                <span className={savingsMode === "ADJUSTED" ? "text-emerald-300" : ""}>Decision-Adj Δ</span>
              </Th>
              <Th align="center">Sourcing Levers</Th>
              <Th align="center">Proof</Th>
            </tr>
          </thead>
          <tbody>
            {!rows && Array.from({ length: 8 }).map((_, i) => (
              <tr key={i} className="border-b border-slate-800">
                <td colSpan="12" className="px-4 py-3">
                  <div className="h-4 skeleton rounded-sm" />
                </td>
              </tr>
            ))}
            {rows && filtered.map((r) => {
              const positive = r.delta >= 0;
              const adjPositive = r.risk_adjusted_delta >= 0;
              return (
                <tr
                  key={r.po_id}
                  data-testid={`ledger-row-${r.po_number}`}
                  className="border-b border-slate-800 hover:bg-slate-800/60 hover:text-slate-50 transition-colors"
                >
                  <td className="px-4 py-2 font-mono text-slate-300">{r.po_number}</td>
                  <td className="px-4 py-2 text-slate-200 max-w-[280px] truncate" title={r.supplier}>{r.supplier}</td>
                  <td className="px-4 py-2 text-slate-400 max-w-[240px] truncate" title={r.agency}>{r.agency}</td>
                  <td className="px-4 py-2 text-slate-300 max-w-[220px] truncate" title={`${r.segment_name} · ${r.segment_code}`}>{r.segment_name}</td>
                  <td className="px-4 py-2 text-center">
                    <span
                      data-testid={`pdi-badge-${r.supplier_id}`}
                      className={`inline-flex items-center justify-center min-w-[36px] px-2 py-0.5 text-[11px] font-mono font-semibold rounded-sm tier-${pdiTier(r.risk_score)}`}
                      title={`PDI ${pdi(r.risk_score)} · Procurement Decision Intelligence`}
                    >
                      {pdi(r.risk_score)}
                    </span>
                  </td>
                  <td className="px-4 py-2">
                    <DriverChip d={drivers[r.segment_code]?.primary} />
                  </td>
                  <td className="px-4 py-2 font-mono text-right text-slate-300">{fmtUSD(r.baseline)}</td>
                  <td className="px-4 py-2 font-mono text-right text-slate-300">{fmtUSD(r.realized)}</td>
                  <td className={`px-4 py-2 font-mono text-right ${savingsMode === "GROSS" ? (positive ? "text-emerald-400" : "text-rose-400") : "text-slate-400"}`}>
                    <span className="inline-flex items-center gap-1 justify-end">
                      {positive ? <ArrowDown weight="bold" size={12} /> : <ArrowUp weight="bold" size={12} />}
                      {fmtUSD(Math.abs(r.delta))}
                      <span className="text-[10px] text-slate-500 ml-1">({fmtPct(r.delta_pct, 1)})</span>
                    </span>
                  </td>
                  <td className={`px-4 py-2 font-mono text-right ${savingsMode === "ADJUSTED" ? (adjPositive ? "text-emerald-300" : "text-rose-400") : "text-slate-500"} ${savingsMode === "ADJUSTED" ? "font-semibold" : ""}`}>
                    <span className="inline-flex items-center gap-1 justify-end" title={`Decision-Adjusted = Gross × ${(pdi(r.risk_score) / 100).toFixed(2)} (PDI ${pdi(r.risk_score)})`}>
                      {adjPositive ? <ArrowDown weight="bold" size={12} /> : <ArrowUp weight="bold" size={12} />}
                      {fmtUSD(Math.abs(r.risk_adjusted_delta))}
                      <span className="text-[10px] text-slate-500 ml-1">×{(pdi(r.risk_score) / 100).toFixed(2)}</span>
                    </span>
                  </td>
                  <td className="px-4 py-2 text-center">
                    <span
                      data-testid={`pdi-badge-${r.supplier_id}`}
                      className={`inline-flex items-center justify-center min-w-[36px] px-2 py-0.5 text-[11px] font-mono font-semibold rounded-sm tier-${pdiTier(r.risk_score)}`}
                      title={`PDI ${pdi(r.risk_score)} · Procurement Decision Intelligence`}
                    >
                      {pdi(r.risk_score)}
                    </span>
                  </td>
                  <td className="px-4 py-2 text-center">
                    <div className="inline-flex items-center gap-1">
                      <button
                        data-testid={`lever-should-cost-${r.po_number}`}
                        onClick={() => onOpenSourcingLever?.("SHOULD_COST", r)}
                        title="Should-Cost decomposition"
                        className="inline-flex items-center gap-1 px-2 py-1 text-[10px] tracking-mono-label text-fuchsia-300 hover:text-fuchsia-200 border border-fuchsia-500/30 hover:border-fuchsia-500/60 rounded-sm transition-colors"
                      >
                        <Calculator weight="bold" size={11} /> Should-Cost
                      </button>
                      <button
                        data-testid={`lever-rate-benchmark-${r.po_number}`}
                        onClick={() => onOpenSourcingLever?.("RATE_BENCHMARK", r)}
                        title="Rate Benchmark"
                        className="inline-flex items-center gap-1 px-2 py-1 text-[10px] tracking-mono-label text-sky-300 hover:text-sky-200 border border-sky-500/30 hover:border-sky-500/60 rounded-sm transition-colors"
                      >
                        <ChartLine weight="bold" size={11} /> Rate
                      </button>
                    </div>
                  </td>
                  <td className="px-4 py-2 text-center">
                    <div className="inline-flex items-center gap-1">
                      <button
                        data-testid={`open-package-${r.po_number}`}
                        onClick={() => openPackage(r)}
                        title={`View Data Package for ${r.segment_name}`}
                        className="inline-flex items-center gap-1 px-2 py-1 text-[10px] tracking-mono-label text-amber-300 hover:text-amber-200 border border-amber-500/30 hover:border-amber-500/60 rounded-sm transition-colors"
                      >
                        <Package weight="bold" size={12} /> Data Package
                      </button>
                      <button
                        data-testid={`open-proof-${r.po_number}`}
                        onClick={() => onOpenSavingsProof(r)}
                        className="inline-flex items-center gap-1 px-2 py-1 text-[10px] tracking-mono-label text-emerald-300 hover:text-emerald-200 border border-emerald-500/30 hover:border-emerald-500/60 rounded-sm transition-colors"
                      >
                        <Eye weight="bold" size={12} /> Proof
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <footer className="px-5 py-2 border-t border-slate-800 flex items-center justify-between text-[11px] font-mono text-slate-500">
        <span>{rows ? `${filtered.length} decision-proof rows · default sort: ${savingsMode === "ADJUSTED" ? "Decision-Adjusted Δ" : "Gross Δ"}` : "Loading…"}</span>
        <span>baselines: locked · ledger: immutable</span>
      </footer>

      {/* Category Data Package drill-down */}
      <CategoryPackageModal
        open={pkgOpen}
        onOpenChange={setPkgOpen}
        segmentCode={pkgSegment.code}
        segmentName={pkgSegment.name}
      />
    </section>
  );
}

function Th({ children, onClick, sort, k, align="left" }) {
  const active = sort && sort.key === k;
  return (
    <th
      onClick={onClick}
      className={`px-4 py-2.5 text-[10px] tracking-mono-label text-slate-500 ${align === "right" ? "text-right" : align === "center" ? "text-center" : "text-left"} ${onClick ? "cursor-pointer hover:text-emerald-300" : ""}`}
    >
      <span className="inline-flex items-center gap-1">
        {children}
        {active && (sort.dir === "asc" ? <ArrowUp size={10} /> : <ArrowDown size={10} />)}
      </span>
    </th>
  );
}

// ─── WORLD ↔ SPEND inline driver chip (per ledger row) ──────────────────────
const SRC_FG = { FRED: "text-amber-300", BLS: "text-sky-300", ECB: "text-violet-300" };
const SRC_LABEL = { FRED: "Macro", BLS: "Labor & Producer", ECB: "Eurozone Macro" };
// Plain-English titles for the technical indicator codes we surface
const DRIVER_TITLE = {
  // BLS Producer / commodity indices
  "WPU101":   "Metals Producer Index",
  "WPU0911":  "Pulp & Paper Index",
  "WPU0571":  "Energy Inputs Index",
  "WPU061":   "Industrial Chemicals Index",
  // BLS Employment Cost / Labor
  "CIS101":   "Labor Cost Index",
  "CIS201":   "Services Wage Index",
  "CEU0500":  "Workforce Earnings Index",
  // FRED macro
  "PCOPPUSDM":"Copper Market Index",
  "PALUMUSDM":"Aluminum Market Index",
  "PNGASUSUSDM": "Natural Gas Index",
  "DCOILBRENTEU":"Crude Oil (Brent) Index",
  "CPIAUCSL": "Consumer Price Index",
  "PPIACO":   "Producer Price Index",
  "DGS10":    "US 10-Year Yield",
  "DTWEXBGS": "Trade-Weighted Dollar",
  // ECB
  "EXR":      "Euro Exchange Rate",
  "HICP":     "Eurozone CPI",
};
function prettyDriver(code) {
  if (!code) return "Market Index";
  // Strip any source prefix already removed; match full code, else fall back to first token
  if (DRIVER_TITLE[code]) return DRIVER_TITLE[code];
  // Best-effort heuristics if a future code lands without a translation
  if (code.startsWith("WPU"))   return "Commodity Index";
  if (code.startsWith("CIS"))   return "Labor Cost Index";
  if (code.startsWith("CEU"))   return "Workforce Earnings Index";
  if (code.startsWith("DGS"))   return "Treasury Yield";
  if (code.startsWith("DCOIL")) return "Crude Oil Index";
  return "Market Index";
}

function DriverChip({ d }) {
  if (!d) return <span className="font-mono text-[10px] text-slate-700">—</span>;
  const up = (d.mom_pct ?? 0) >= 0;
  const flat = d.trend === "flat";
  const momColor = flat ? "text-slate-400" : up ? "text-emerald-300" : "text-rose-300";
  const label = prettyDriver(d.code);
  return (
    <div
      data-testid={`ledger-driver-${d.code}`}
      title={`${d.label || label} · ${d.source} ${d.code}${d.narrative ? " · " + d.narrative : ""}`}
      className="inline-flex items-center gap-1.5 font-mono text-[10px]"
    >
      <span className={`tracking-mono-label ${SRC_FG[d.source] || "text-slate-300"}`}>
        {label}
      </span>
      <span className={momColor}>
        {d.trend === "falling" ? "▼" : d.trend === "rising" ? "▲" : "·"}
        {(d.mom_pct ?? 0) >= 0 ? "+" : ""}{(d.mom_pct ?? 0).toFixed(1)}%
      </span>
    </div>
  );
}
